import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import APIKit from "../../config/APIKit";

export const fetchProducts = createAsyncThunk(
  "product/fetchProducts",
  async ({ categoryID, page = 1 }, { rejectWithValue }) => {
    try {
      const response = await APIKit.get(`product/list/${categoryID}`, {
        params: {
          page: page,
        },
      });
      return response.data;
    } catch (error) {
      // Check if it's a network error
      if (error?.isNetworkError || error?.code === 'NETWORK_ERROR') {
        return rejectWithValue({ 
          type: 'NETWORK_ERROR', 
          message: 'Unable to load products. Please check your internet connection.' 
        });
      }
      
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Products get failed");
      }
      return rejectWithValue("Products get failed");
    }
  }
);

export const searchProducts = createAsyncThunk(
  "product/searchProducts",
  async ({ text }, { rejectWithValue }) => {
    try {
      
      const response = await APIKit.get(`product/search`, {
        params: {
          q: text,
        },
      });
      return response.data;
    } catch (error) {
      // Check if it's a network error
      if (error?.isNetworkError || error?.code === 'NETWORK_ERROR') {
        return rejectWithValue({ 
          type: 'NETWORK_ERROR', 
          message: 'Unable to search products. Please check your internet connection.' 
        });
      }
      
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Search failed");
      }
      return rejectWithValue("Search failed");
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState: {
    products: [],
    status: "idle",
    error: null,
    isSearchMode: false,
    searchQuery: "",
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  },
  reducers: {
    clearProducts: (state) => {
      state.products = [];
      state.status = "idle";
      state.error = null;
      state.isSearchMode = false;
      state.searchQuery = "";
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      };
    },
    setSearchMode: (state, action) => {
      state.isSearchMode = action.payload;
    },
    clearSearchMode: (state) => {
      state.isSearchMode = false;
      state.searchQuery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isSearchMode = false;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isSearchMode = false;
        // Handle both array and paginated response
        if (Array.isArray(action.payload)) {
          state.products = action.payload;
        } else if (action.payload.data && Array.isArray(action.payload.data)) {
          state.products = action.payload.data;
          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.currentPage || 1,
              totalPages: action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.totalItems || 0,
              itemsPerPage: action.payload.pagination.itemsPerPage || 10,
            };
          }
        } else {
          state.products = action.payload;
        }
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isSearchMode = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Products get failed";
      })
      .addCase(searchProducts.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isSearchMode = true;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isSearchMode = true;
        // Handle both array and paginated response for search
        if (Array.isArray(action.payload)) {
          state.products = action.payload;
        } else if (action.payload.data && Array.isArray(action.payload.data)) {
          state.products = action.payload.data;
          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.currentPage || 1,
              totalPages: action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.totalItems || 0,
              itemsPerPage: action.payload.pagination.itemsPerPage || 10,
            };
          }
        } else {
          state.products = action.payload;
        }
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isSearchMode = true;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Search failed";
      });
  },
});

export const { clearProducts, setSearchMode, clearSearchMode } = productSlice.actions;
export default productSlice.reducer;
